import { memo, useCallback, useContext, useEffect, useState } from "react";
import OptionFormatter from "../../../../components/OptionFormatter";
import { ProductMaster } from "../../../../hooks/useProductMaster";
import {
  IGlobalProduct,
  ISkuInformation,
  ITcc,
  PisRequestStatus,
  Sku,
  TccCompositionRegionInfo,
} from "../../../../types/data.interface";
import { Tooltip } from "react-tooltip";
import { genericOptionFormatter } from "../../helpers/genericOptionFormater";
import { useCreateMpnStore } from "../../context/create/CreateMpnContext";
import { NEW_PIS_HIERARCHY_PREFIX } from "../../../../types/persistent-page-filters";
import { Predicates } from "../../../../libraries/predicates/predicates";
import useSku, { fecthSku } from "../../../../hooks/useSku";
import useAxios from "../../../../utils/useAxios";
import { getSku } from "../../../../helpers/sku.helper";
import Spinner from "../../../../components/Spinner";
import AsyncSelect from "react-select/async";
import ProductMasterTable from "routes/mpn/components/ProductMasterTable";
import { CreateMpnActions } from "../../states/create";
import { CreateMpnActionTypes } from "../../states/create/data";
import { DebounceContext, DebounceContextType } from "context/DebounceContext";
import useTcc, { fetchTcc } from "../../../../hooks/useTcc";
import {
  getItemNumber,
  getItemNumberDisplayCode,
} from "../../../../helpers/tcc.helper";
import {
  formatGlobalProductIntoOption,
  getGlobalProduct,
} from "../../../../helpers/global-product.helper";
import { fetchGlobalProductWithGPCode } from "../../../../hooks/useGlobalProduct";
import { formatDatacalogIntoOption, getDatacatalog } from "../../../../helpers/datacatalog.helper";
import Select from "react-select";
import { useDatacatalogContext } from "../../../../context/DatacatalogContext";

export type HierarchyProps = {
  selectedSku: Sku | null;
  setSelectedSku: (pm: Sku | null) => void;
  selectedPM: ProductMaster | null;
  setSelectedPM: (pm: ProductMaster | null) => void;
  selectedGP: IGlobalProduct | null;
  setSelectedGP: (gp: IGlobalProduct | null) => void;
  selectedTcc: ITcc | null;
  setSelectedTcc: (tcc: ITcc | null) => void;
  requestStatus: PisRequestStatus;
  requestRevision: string;
  setUnsavedChangesToTrue: () => void;
  selectedTccCompositionRegion: TccCompositionRegionInfo | null;
  setSelectedTccCompositionRegion: (
    tccCompositionRegion: TccCompositionRegionInfo | null,
  ) => void;
  selectableTccCompositionRegions: TccCompositionRegionInfo[] | null;
  skuData: ISkuInformation;
  setSkuData: (data: ISkuInformation) => void;

  setChangedImportValues: (didChange: boolean) => void;
  setSelectedTccCompositionRows: (rows: number[]) => void;
  setSelectedMoldIndexes: (indexes: string[]) => void;
  isReadOnly: boolean;
};

interface IHierarchyProps {
  handleSelectItemNumber: any;
  handleSelectGlobalProduct: () => void;
  requestStatus: any;
  isReadOnly: any;
  isLoading: boolean;
  setUnsavedChangesToTrue: () => void;
}

const Hierarchy = memo(
  ({
    handleSelectItemNumber,
     handleSelectGlobalProduct,
    requestStatus,
    isReadOnly,
    isLoading = true,
    setUnsavedChangesToTrue
  }: IHierarchyProps) => {
    const { state, dispatch } = useCreateMpnStore();

    const [sku, setSku] = useState<Sku>();
    const { searchDebounce } = useContext<DebounceContextType>(DebounceContext);

    const [selectedPM, setSelectedPM] = useState<ProductMaster>({} as ProductMaster);

    const axios = useAxios();

    const { data: skus, isLoading: isSkusLoading } = useSku({
      // search: selectedTcc ? selectedTcc.id?.toString() : undefined,
      search: undefined,
    });

    const { data: tccs, isLoading: isTccsLoading } = useTcc({
      global_product: state?.global_product?.id,
    });

    const handleSku = useCallback((item: any) => {
      setSku(item?.value ?? null);
      dispatch(
        CreateMpnActions.setStateValue(
          CreateMpnActionTypes.SET_MPN_TO_CLONE,
          !!item
            ? {
                ...item?.value,
              }
            : null,
        ),
      );

        dispatch(
          CreateMpnActions.setStateValue(
            CreateMpnActionTypes.SET_GLOBAL_PRODUCT,
            !!item
              ? item.value?.global_product
              : null,
          ),
        );

        dispatch(
          CreateMpnActions.setStateValue(
            CreateMpnActionTypes.SET_ITEM_NUMBER,
            !!item
              ? item.value?.tcc
              : null,
          ),
        );

      if (item && (item.value?.tcc || item.value?.global_product)) {
        setSelectedPM({
          id: item.value.id,
          description: item.value.name.trim(),
          image_file: item.value.image_file,
          inventory: item.value.invetory,
          mold_qty: item.value.mold_qty,
          stdpk: item.value.stdpk,
        });
      }

      setUnsavedChangesToTrue();
    }, []);


    const skuOptions = async (search: string, callback: any) => {
      if (Predicates.isNullOrUndefined(search) || search.length < 3) return [];
      const response = await fecthSku({
        search,
        axios,
      });
      callback(getSku(response));
    };

    const { datacatalog, isLoading: isDatacatalogLoading } =
      useDatacatalogContext();

    const itemNumberOptions = async (search: string, callback: any) => {
      const response = await fetchTcc({
        category: state.category?.code,
        sub_category: state.sub_category?.code,
        product_line: state.product_line?.code,
        global_product: state.global_product?.code,
        search,
        axios,
      });
      callback(getItemNumber(response));
    };

    const [isGpLoading, setIsGpLoading] = useState(true);

    const globalProductOptionsWithGPCode = async (
      search: string,
      callback: any,
    ) => {
      if (!search || search.length < 3) return [];
      setIsGpLoading(true);
      const response = await fetchGlobalProductWithGPCode({
        category: state.sub_category?.parent?.id ?? state.category?.id,
        sub_category: state.sub_category?.id,
        product_line: state.product_line?.id,
        search,
        axios,
      });

      const options = getGlobalProduct(response, {
        subCategory: state.sub_category?.id,
        productLine: state.product_line?.id,
        id: state.global_product?.id,
      });

      callback(options);
      setIsGpLoading(false)
    };

    const [filteredGPOptions, setFilteredGPOptions] = useState<any[]>([]);
    const [zeroGPOptions, setZeroGPOptions] = useState<any[]>([]);

    useEffect(() => {
      const loadFiltered = async () => {
        const resp = await fetchGlobalProductWithGPCode({
          category: state.sub_category?.parent?.id ?? state.category?.id,
          sub_category: state.sub_category?.id,
          product_line: state.product_line?.id,
          axios,
        });

        const opts = getGlobalProduct(resp, {
          subCategory: state.sub_category?.id,
          productLine: state.product_line?.id,
          id: state.global_product?.id,
        });

        setFilteredGPOptions(opts);
        setIsGpLoading(false)
      };

      loadFiltered();
    }, [state.category, state.sub_category, state.product_line]);

    return (
      <>
        {isLoading && <Spinner />}
        {!isLoading && (
          <>
            <h6 className="text-primary fw-bold card-header border-0 mb-2">
              MPN Hierarchy
            </h6>
            <div className="my-3 row">
              <div
                className="col-auto d-flex flex-column justify-content-start"
                style={{ marginTop: 2 }}
              >
                <label className="form-label">Clone hierarchy from MPN:</label>
              </div>
              <div className="col-sm-6 col-md-5">
                <div
                  className={`d-flex align-items-center ${
                    Predicates.isNotNullAndNotUndefined(sku) &&
                    Predicates.isNullOrUndefined(
                      //@ts-ignore
                      sku?.global_product?.gpc_gpcode,
                    )
                      ? "is-invalid"
                      : ""
                  }`}
                >
                  <span className="form-label mr-2">11 </span>
                  <AsyncSelect
                    name="mpn-clone"
                    value={
                      Predicates.isNotNullAndNotUndefined(state?.mpnToClone?.id)
                        ? {
                            value: state?.mpnToClone,
                            label: `${state?.mpnToClone?.id} - ${state?.mpnToClone.description ?? state?.mpnToClone.name}`,
                          }
                        : null
                    }
                    onChange={handleSku}
                    defaultOptions={getSku(skus)}
                    loadOptions={(input, callback) => {
                      searchDebounce(skuOptions, input, getSku(skus), callback);
                    }}
                    formatOptionLabel={OptionFormatter}
                    isLoading={isSkusLoading}
                    cacheOptions
                    isClearable
                    isSearchable
                    placeholder="(min 3 digits)"
                    classNamePrefix="react-select"
                    className={`flex-fill
                    ${
                      // Predicates.isNotNullAndNotUndefined(state?.mpnToClone?.id) &&
                      Predicates.isNotNullAndNotUndefined(sku) &&
                      Predicates.isNullOrUndefined(
                        //@ts-ignore
                        sku?.global_product?.gpc_gpcode,
                      )
                        ? "is-invalid"
                        : ""
                    }
                      `}
                    // isDisabled={true}
                  />
                </div>
                <div className="invalid-feedback ml-4">
                  This MPN has no Global Product associated, please select
                  another MPN to clone the hierarchy.
                </div>
              </div>
            </div>
            <div className="row mb-2">
              <div className="col-6 col-md-4">
                <label className="form-label">Category</label>
                <Select
                  options={getDatacatalog(datacatalog, { type: "Category" })}
                  onChange={(e) => {
                    dispatch(
                      CreateMpnActions.setStateValue(
                        CreateMpnActionTypes.SET_CATEGORY,
                        !!e ? e.value : null,
                      ),
                    );
                  }}
                  formatOptionLabel={OptionFormatter}
                  value={
                    state.category
                      ? formatDatacalogIntoOption(state.category)
                      : null
                  }
                  classNamePrefix="react-select"
                  placeholder=""
                  isClearable
                  isLoading={isDatacatalogLoading}
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  isDisabled={true}
                />
              </div>
              <div className="col-6 col-md-4">
                <label className="form-label">Sub-Category</label>
                <Select
                  options={getDatacatalog(datacatalog, {
                    type: "Sub-Category",
                    categoryId: state.category?.id,
                  })}
                  onChange={(e) => {
                    dispatch(
                      CreateMpnActions.setStateValue(
                        CreateMpnActionTypes.SET_SUB_CATEGORY,
                        !!e ? e.value : null,
                      ),
                    );
                  }}
                  formatOptionLabel={OptionFormatter}
                  value={
                    state.sub_category
                      ? formatDatacalogIntoOption(state.sub_category)
                      : null
                  }
                  classNamePrefix="react-select"
                  placeholder=""
                  isClearable
                  isLoading={isDatacatalogLoading}
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  isDisabled={true}
                />
              </div>
              <div className="col-6 col-md-4">
                <label className="form-label">Product Line</label>
                <Select
                  options={getDatacatalog(datacatalog, {
                    type: "Product Line",
                  })}
                  onChange={(e) => {
                    dispatch(
                      CreateMpnActions.setStateValue(
                        CreateMpnActionTypes.SET_PRODUCT_LINE,
                        !!e ? e.value : null,
                      ),
                    );
                  }}
                  value={
                    state.product_line
                      ? formatDatacalogIntoOption(state.product_line)
                      : null
                  }
                  formatOptionLabel={OptionFormatter}
                  classNamePrefix="react-select"
                  placeholder=""
                  isClearable
                  isLoading={isDatacatalogLoading}
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  isDisabled={true}
                />
              </div>
            </div>
            <div className="row mb-2">
              <div className="col-md-9">
                <label className="form-label">Global Product</label>

                <AsyncSelect
                  name="globalProduct"
                  value={
                    state.global_product
                      ? formatGlobalProductIntoOption(state.global_product)
                      : null
                  }
                  onChange={handleSelectGlobalProduct}
                  defaultOptions={filteredGPOptions}
                  loadOptions={(input, callback) => {
                    searchDebounce(
                      globalProductOptionsWithGPCode,
                      input,
                      filteredGPOptions,
                      callback,
                    );
                  }}
                  formatOptionLabel={OptionFormatter}
                  isClearable
                  isSearchable
                  isLoading={isGpLoading}
                  placeholder="(min 3 characters)"
                  classNamePrefix="react-select"
                />
              </div>

              <div className="col-3 form-group">
                <Tooltip
                  id="gp-lifecycle"
                  className="tooltip-text-box"
                  place="top"
                  noArrow={true}
                />
                <label className="form-label">GP Lifecycle Status</label>
                <input
                  type="text"
                  className="form-control horizontal-scroll"
                  value={
                    state.global_product
                      ? `${state.global_product?.production_lifecycle_status}`
                      : ""
                  }
                  disabled={true}
                />
              </div>
            </div>
            <div className="row mb-2">
              <div className="col-6 col-md-12">
                <label className="form-label">Item Number / TCC</label>
                <AsyncSelect
                  name="itemNumber"
                  value={
                    Predicates.isNotNullAndNotUndefinedAndNotEmpty(
                      state.itemNumber?.name,
                    )
                      ? genericOptionFormatter(
                          state.itemNumber.itn_itemno ||
                            getItemNumberDisplayCode(state.itemNumber),
                          state.itemNumber.name,
                          state.itemNumber,
                        )
                      : null
                  }
                  onChange={handleSelectItemNumber}
                  isDisabled={isReadOnly}
                  defaultOptions={getItemNumber(tccs)}
                  loadOptions={(input, callback) => {
                    searchDebounce(
                      itemNumberOptions,
                      input,
                      getItemNumber(tccs),
                      callback,
                    );
                  }}
                  formatOptionLabel={OptionFormatter}
                  isLoading={isTccsLoading}
                  isClearable
                  isSearchable
                  placeholder="(min 3 characters)"
                  classNamePrefix="react-select"
                />
              </div>
            </div>
            <h6 className="text-primary card-header fw-bold  border-0 mb-1">
              {`MPNs included in above selection`}
            </h6>
            <ProductMasterTable
              localStoragePrefix={NEW_PIS_HIERARCHY_PREFIX}
              checkUnsaved={true}
              filters={{
                itemNumberId: state?.itemNumber?.id,
                globalProductId: state?.global_product?.id,
              }}
              onSelectionChanged={handleSku}
              enableAdvancedFilters={true}
              showClearButton={false}
              showCustomFields={["tcc_name"]}
              showSelectColumn={true}
            />{" "}
          </>
        )}
      </>
    );
  },
);

export default Hierarchy;
